import { SortDirection } from "@redotech/redo-views-common/table";
import * as z from "zod";

export const ConsolidationSummaryFilterNames = {
  destinationLocationId: "destinationLocationId",
  dueAt: "dueAt",
  kind: "kind",
  laneId: "laneId",
  merchantAccountId: "merchantAccountId",
  originLocationId: "originLocationId",
  status: "status",
} as const;

/** Non-overlapping buckets of a consolidation's close time relative to now. */
export type ConsolidationDueWindow =
  | "overdue"
  | "today"
  | "next_7_days"
  | "later"
  | "unscheduled";

export const ConsolidationSortKeySchema = z.enum([
  "closeDueAt",
  "itemCount",
  "lastActivityAt",
]);
export type ConsolidationSortKey = z.infer<typeof ConsolidationSortKeySchema>;

export const ConsolidationSortSchema = z.object({
  direction: z.enum(SortDirection),
  key: ConsolidationSortKeySchema,
});
export type ConsolidationSort = z.infer<typeof ConsolidationSortSchema>;

export const CONSOLIDATION_DUE_SOON_DAYS = 7;

export interface ConsolidationDueWindowBounds {
  now: Date;
  /** Last instant of today in the warehouse timezone. */
  endOfToday: Date;
  /** Last instant of the day {@link CONSOLIDATION_DUE_SOON_DAYS} days from today. */
  endOfDueSoon: Date;
}

export function consolidationDueWindowBounds(
  now: Date,
  timeZone: string,
): ConsolidationDueWindowBounds {
  const today = Temporal.Instant.fromEpochMilliseconds(now.getTime())
    .toZonedDateTimeISO(timeZone)
    .toPlainDate();
  return {
    endOfDueSoon: endOfDay(
      today.add({ days: CONSOLIDATION_DUE_SOON_DAYS }),
      timeZone,
    ),
    endOfToday: endOfDay(today, timeZone),
    now,
  };
}

export function consolidationDueWindow(
  dueAt: Date | null,
  bounds: ConsolidationDueWindowBounds,
): ConsolidationDueWindow {
  if (!dueAt) {
    return "unscheduled";
  }
  const dueAtMs = dueAt.getTime();
  if (dueAtMs < bounds.now.getTime()) {
    return "overdue";
  }
  if (dueAtMs <= bounds.endOfToday.getTime()) {
    return "today";
  }
  if (dueAtMs <= bounds.endOfDueSoon.getTime()) {
    return "next_7_days";
  }
  return "later";
}

function endOfDay(date: Temporal.PlainDate, timeZone: string): Date {
  const startOfNextDay = date
    .add({ days: 1 })
    .toZonedDateTime({ timeZone })
    .toInstant();
  return new Date(startOfNextDay.epochMilliseconds - 1);
}
